import { useEffect, useRef, useState } from "react";

import { Link, withRouter } from "react-router-dom";
import Logo from "../../assets/images/svg/logo/utivacolored.svg";

import useFBQPageView from "../../helpers/useFBQPageView";
import { ArrowUpRight, HamburgerIcon } from "../svgs";
import { aboutMenuData } from "./data/aboutMenuData";
import AboutDropdown from "./dropdowns/AboutDropdown";
import SchoolsDropdown from "./dropdowns/SchoolsDropdown";
import MobileMenu from "./mobileNav/MobileMenu";

const HeaderNavigation = ({ bgdark, bannerHeight = 0, ...props }) => {
  const [activeMenu, setActiveMenu] = useState(null);

  const [openMenu, setOpenMenu] = useState(false);

  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);
  const [navTop, setNavTop] = useState(bannerHeight);

  useEffect(() => {
    const measure = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        setNavHeight(height);
        // Exposed so other fixed-header-aware elements (e.g. a sticky
        // section sub-nav) can offset themselves below the fixed header
        // without re-measuring it themselves.
        document.documentElement.style.setProperty(
          "--header-height",
          `${height}px`
        );
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeMenu, openMenu]);

  useEffect(() => {
    if (bannerHeight <= 0) {
      setNavTop(0);
      return;
    }

    let ticking = false;
    const updateTop = () => {
      setNavTop(Math.max(0, bannerHeight - window.scrollY));
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateTop);
      }
    };

    updateTop();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [bannerHeight]);

  const toggleMenu = (menu) => {
    setActiveMenu((current) => (current === menu ? null : menu));
  };

  const handleCloseMenu = () => {
    setOpenMenu(!openMenu);
    setActiveMenu(null);
  };

  useEffect(() => {
    handleCloseMenu();
    setOpenMenu(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.pathname]);

  const handleClose = () => {
    setActiveMenu(null);
  };

  //   Facebook Pixel
  useFBQPageView();

  const pathname = props.location.pathname;
  const isAboutSection = aboutMenuData.some((item) => item.url === pathname);

  const navUnderlineClass = (active) =>
    `border-b-2 pb-1 transition-colors duration-150 ${
      active ? "border-secondary" : "border-transparent"
    } group-hover:border-secondary`;

  return (
    <>
      <div
        ref={navRef}
        className="fixed bg-white/95 backdrop-blur-md border-b border-hire-line inset-x-0 z-30"
        style={{ top: navTop }}
      >
        {/* <HeaderContinentSelectSection /> */}
        <div className={`container mx-auto px-4 text-default `}>
          <div className="flex justify-between items-center py-4 md:justify-start">
            <div className="hidden sm-hidden md:block lg:hidden xl:hidden">
              <button
                type="button"
                onClick={handleCloseMenu}
                className="rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open menu</span>

                <HamburgerIcon color={"#050794"} />
              </button>
            </div>
            <div className="flex justify-start">
              <Link to="/">
                <span className="sr-only">Utiva</span>
                <img className="h-7 w-auto sm:h-9" src={Logo} alt="" />
              </Link>
            </div>
            <div className="-mr-2 -my-2 md:hidden lg:hidden">
              <button
                type="button"
                onClick={handleCloseMenu}
                className="rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open menu</span>

                <HamburgerIcon color={"#050794"} />
              </button>
            </div>
            <div className="ml-auto flex items-center gap-6">
              <nav className="hidden lg:flex flex-none items-center gap-5 text-[#3d4356]">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleMenu("learn")}
                    aria-expanded={activeMenu === "learn"}
                    className={`group inline-flex items-center gap-1.5 rounded-xl border text-sm font-poppins font-extrabold py-2.5 px-4 shadow-sm transition-all focus:outline-none ${
                      activeMenu === "learn"
                        ? "bg-primary border-primary text-white"
                        : "bg-secondary border-secondary text-primary hover:bg-primary hover:border-primary hover:text-white"
                    }`}
                  >
                    Learn
                    <span
                      aria-hidden="true"
                      className={`text-xs transition-transform duration-150 ${
                        activeMenu === "learn" ? "rotate-180" : ""
                      }`}
                    >
                      ⌄
                    </span>
                  </button>

                  <SchoolsDropdown
                    isOpen={activeMenu === "learn"}
                    handleClose={handleClose}
                    topOffset={navTop + navHeight}
                  />
                </div>

                <div className="relative">
                  <Link
                    to="/hire"
                    className="group inline-flex items-center text-sm font-poppins font-semibold focus:outline-none"
                  >
                    <span className={navUnderlineClass(pathname === "/hire")}>
                      Hire Talent
                    </span>
                  </Link>
                </div>

                <div className="relative">
                  <Link
                    to="/pay"
                    className="group inline-flex items-center text-sm font-poppins font-semibold focus:outline-none"
                  >
                    <span className={navUnderlineClass(pathname === "/pay")}>
                      Manage &amp; Pay
                    </span>
                  </Link>
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleMenu("about")}
                    className="group inline-flex items-center gap-1 text-sm font-poppins font-semibold focus:outline-none"
                    aria-expanded={activeMenu === "about"}
                  >
                    <span
                      className={navUnderlineClass(
                        activeMenu === "about" || isAboutSection
                      )}
                    >
                      About Utiva
                    </span>
                    <span
                      aria-hidden="true"
                      className={`text-xs text-primary transition-transform duration-150 ${
                        activeMenu === "about" || isAboutSection
                          ? "rotate-180"
                          : ""
                      }`}
                    >
                      ⌄
                    </span>
                  </button>

                  <AboutDropdown
                    isAboutOpen={activeMenu === "about"}
                    handleClose={handleClose}
                    topOffset={navTop + navHeight}
                  />
                </div>

                <div className="relative">
                  <Link
                    to="/blog"
                    className="group inline-flex items-center text-sm font-poppins font-semibold focus:outline-none"
                  >
                    <span className={navUnderlineClass(pathname === "/blog")}>
                      Resources
                    </span>
                  </Link>
                </div>
              </nav>
              <div className="hidden md:flex items-center gap-6">
                <a
                  href="https://app.utiva.co/"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group whitespace-nowrap text-sm font-extrabold font-poppins text-primary focus:outline-none"
                >
                  <span className={navUnderlineClass(false)}>Log in</span>
                </a>

                {pathname !== "/" && (
                  <Link
                    to="/start"
                    className="bg-primary text-secondary font-poppins font-extrabold rounded-xl inline-flex items-center gap-2 px-5 py-3 text-sm custom-btn focus:outline-none"
                  >
                    Explore programs
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* <!--
    Mobile menu, show/hide based on mobile menu state.
  --> */}
        <MobileMenu openMenu={openMenu} handleCloseMenu={handleCloseMenu} />
      </div>
      <div style={{ height: navHeight }} aria-hidden="true" />
    </>
  );
};

export default withRouter(HeaderNavigation);
